// Saved parties, kept in the browser between sessions.
//
// A save holds the characters themselves — levels, experience, gear and
// condition — along with the gold they are carrying, so a party can go back
// into the dungeon another day.

import { recalculate } from './party';

const STORE_KEY = 'frpg.parties';
export const MAX_SAVES = 8;
export const MAX_NAME = 24;

function read() {
    try {
        const raw = window.localStorage.getItem(STORE_KEY);
        const saves = raw ? JSON.parse(raw) : [];
        return Array.isArray(saves) ? saves : [];
    } catch (err) {
        // Unreadable or blocked storage: carry on with no saves
        return [];
    }
}

function write(saves) {
    try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(saves));
        return true;
    } catch (err) {
        console.warn('Could not save the party', err);
        return false;
    }
}

export function tidyName(name) {
    return String(name || '').trim().slice(0, MAX_NAME);
}

// Newest first, so the launch menu lists the last party played at the top
export function listParties() {
    return read().sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
}

export function findParty(name) {
    const wanted = tidyName(name).toLowerCase();
    return listParties().find((save) => save.name.toLowerCase() === wanted) || null;
}

// A short line for the menu: who these adventurers are
export function describeParty(save) {
    const levels = save.party.map((c) => c.level);
    const lowest = Math.min(...levels);
    const highest = Math.max(...levels);
    const fallen = save.party.filter((c) => c.condition === 'dead').length;
    const level = lowest === highest ? `level ${highest}` : `levels ${lowest}-${highest}`;
    return `${level} · ${save.gold} gold${fallen ? ` · ${fallen} dead` : ''}`;
}

// Save under a name, replacing any party already saved under it
export function saveParty(name, party, gold, extra = {}) {
    const tidy = tidyName(name);
    if (!tidy) return null;

    const saves = read().filter((existing) => existing.name.toLowerCase() !== tidy.toLowerCase());
    const save = {
        name: tidy,
        party,
        gold,
        savedAt: Date.now(),
        runs: (extra.runs || 0) + 1,
        deepest: extra.deepest || 0,
    };
    // Stored newest first, so trimming always drops the oldest — saves made in
    // the same millisecond would otherwise tie and sort arbitrarily
    return write([save, ...saves].slice(0, MAX_SAVES)) ? save : null;
}

export function deleteParty(name) {
    const wanted = tidyName(name).toLowerCase();
    return write(read().filter((save) => save.name.toLowerCase() !== wanted));
}

// The characters from a save, with their derived numbers worked out again
export function partyFromSave(save) {
    return save.party.map((character) => recalculate({
        ...character,
        // Anyone who was knocked out gets back on their feet between runs
        condition: character.condition === 'dead' ? 'dead' : 'good',
        hp: character.condition === 'dead' ? character.hp : Math.max(1, character.hp),
    }));
}
